import { isDefined } from "@clipboard-health/util-ts";
import { get, useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { z } from "zod";

export interface GetTimezoneParams {
  latitude: number | undefined;
  longitude: number | undefined;
}

const getTimezoneRequestSchema = z.object({
  filter: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
});

export type GetTimezoneRequest = z.infer<typeof getTimezoneRequestSchema>;

const getTimezoneResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.string(),
    attributes: z.object({
      name: z.string(),
    }),
  }),
});

export type GetTimezoneResponse = z.infer<typeof getTimezoneResponseSchema>;

export const GET_TIMEZONE_API_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/timezone`;

export async function getTimezone(params: GetTimezoneParams) {
  const { latitude, longitude } = params;
  return await get({
    url: GET_TIMEZONE_API_URL,
    queryParams: {
      filter: {
        latitude,
        longitude,
      },
    },
    requestSchema: getTimezoneRequestSchema,
    responseSchema: getTimezoneResponseSchema,
  });
}

/**
 * This uses a throttled paid API (~$5 per 1000 API) request so be mindful not to cause infinite loops
 */
export function useGetTimezone(
  params: GetTimezoneParams,
  options: UseGetQueryOptions<GetTimezoneResponse> = {}
) {
  const { latitude, longitude } = params;
  return useGetQuery({
    url: GET_TIMEZONE_API_URL,
    requestSchema: getTimezoneRequestSchema,
    responseSchema: getTimezoneResponseSchema,
    queryParams: {
      filter: {
        latitude,
        longitude,
      },
    },
    enabled: isDefined(latitude) && isDefined(longitude),

    // For given latitude and longitude, timezone won't change for browser session
    // we can set staleTime and cacheTime to Infinity
    staleTime: Number.POSITIVE_INFINITY,
    cacheTime: Number.POSITIVE_INFINITY,

    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_TIMEZONE_FAILURE,
      userErrorMessage: "Failed to get timezone",
    },
    ...options,
  });
}
